import { useEffect, useRef, useState } from "react";
import { Layer, Source, useMap } from "react-map-gl/mapbox";

import {
  BOUNDARY_LINE_LAYER_ID,
  ORTHOPHOTO_IMAGE_LAYER_ID,
  ORTHOPHOTO_OUTLINE_LAYER_ID,
} from "components/PlotMap/utils/layerPrefs";
import { EMPTY_FEATURE_COLLECTION } from "components/PlotMap/utils/mapConstants";
import { useCogOrtho } from "context/CogOrtho";
import {
  DETAIL_ZOOM_ENTER,
  DETAIL_ZOOM_EXIT,
  preloadImageUrls,
  VIEWPORT_TILE_RETAIN_RATIO,
  type ImageCornerCoordinates,
  type OrthoDetailTile,
} from "context/CogOrtho/orthoAssets";
import { useOrthoLodDebug } from "context/OrthoLodDebug";

interface CogOrthoMapLayersProps {
  orthoId: number | null;
  reportLodDebug?: boolean;
  visibility: "none" | "visible";
}

const ORTHOPHOTO_OUTLINE_SOURCE_ID = "odm-orthophoto-outline";
const ORTHOPHOTO_OVERVIEW_SOURCE_ID = "odm-orthophoto-overview";

/** 1×1 transparent PNG so the overview image source can stay mounted. */
const EMPTY_ORTHO_IMAGE_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR4nGNgAAIAAAUAAXpeqz8AAAAASUVORK5CYII=";

const EMPTY_ORTHO_IMAGE_COORDINATES: ImageCornerCoordinates = [
  [0, 0],
  [0.0001, 0],
  [0.0001, -0.0001],
  [0, -0.0001],
];

// Stable like overview/outline: this component is reused when switching orthos.
// react-map-gl throws if a mounted <Source> id changes; url/coordinates update in place.
const getDetailTileLayerId = (tileId: string): string =>
  `odm-orthophoto-tile-raster-${tileId}`;

const getDetailTileSourceId = (tileId: string): string =>
  `odm-orthophoto-tile-${tileId}`;

/**
 * Renders the current COG ortho on the map (overview + viewport detail tiles).
 * The outline is inserted under the grower boundary. The overview image and
 * detail tiles are inserted under the outline, so a late image load cannot
 * cover the plot stack. Tiles do not overlap, so their order does not matter.
 */
const CogOrthoMapLayers: React.FC<CogOrthoMapLayersProps> = ({
  orthoId,
  reportLodDebug = false,
  visibility,
}) => {
  const { default: mapRef } = useMap();
  const { sessions } = useCogOrtho();
  const session = orthoId == null ? undefined : sessions[orthoId];
  const cogOrthoApi = session?.api ?? null;
  const orthoOverlay = session?.overlay ?? null;
  const sessionKey = session?.sessionKey ?? null;
  const { setOrthoLodDebug } = useOrthoLodDebug();
  const layerVisibility =
    orthoOverlay == null || visibility === "none" ? "none" : "visible";

  const lastZoomThresholdLogAtRef = useRef(0);
  const tileLoadGenerationRef = useRef(0);
  const wantDetailRef = useRef(false);

  const [mapZoom, setMapZoom] = useState(0);
  const [visibleTiles, setVisibleTiles] = useState<OrthoDetailTile[]>([]);
  const [wantDetail, setWantDetail] = useState(false);

  // State drives effect deps and debug UI; ref gives async/map callbacks the latest
  // value so in-flight tile loads don't publish after zoom drops below DETAIL_ZOOM_EXIT.
  wantDetailRef.current = wantDetail;
  const lodMode =
    wantDetail && visibleTiles.length > 0 ? "detail-full" : "overview";
  useEffect(() => {
    const map = mapRef?.getMap();
    if (map == null) {
      return;
    }

    const updateLodFromZoom = (zoom: number) => {
      setMapZoom(zoom);
      setWantDetail((current) => {
        let next = current;
        if (!current && zoom >= DETAIL_ZOOM_ENTER) {
          next = true;
        } else if (current && zoom <= DETAIL_ZOOM_EXIT) {
          next = false;
        }

        if (next !== current) {
          console.debug(
            "[CogOrthoMapLayers] wantDetail changed",
            JSON.stringify({
              detailZoomEnter: DETAIL_ZOOM_ENTER,
              detailZoomExit: DETAIL_ZOOM_EXIT,
              from: current,
              to: next,
              zoom,
            }),
          );
        } else {
          const now = performance.now();
          if (now - lastZoomThresholdLogAtRef.current > 500) {
            lastZoomThresholdLogAtRef.current = now;
            console.debug(
              "[CogOrthoMapLayers] zoom vs detail threshold",
              JSON.stringify({
                deltaToEnter: DETAIL_ZOOM_ENTER - zoom,
                detailZoomEnter: DETAIL_ZOOM_ENTER,
                detailZoomExit: DETAIL_ZOOM_EXIT,
                wantDetail: current,
                zoom,
              }),
            );
          }
        }

        return next;
      });
    };

    const handleZoomChange = () => {
      updateLodFromZoom(map.getZoom());
    };

    handleZoomChange();
    map.on("move", handleZoomChange);
    map.on("zoom", handleZoomChange);

    return () => {
      map.off("move", handleZoomChange);
      map.off("zoom", handleZoomChange);
    };
  }, [mapRef]);

  useEffect(() => {
    if (!reportLodDebug) {
      return;
    }

    const publishMemoryDebug = () => {
      const memory = cogOrthoApi?.getMemoryStats();
      setOrthoLodDebug({
        detailCacheBlobBytes: memory?.detailCacheBlobBytes ?? 0,
        detailCachePeakBlobBytes: memory?.detailCachePeakBlobBytes ?? 0,
        detailCacheSize: memory?.detailCacheSize ?? 0,
        geotiffBlockCacheBudgetBytes: memory?.geotiffBlockCacheBudgetBytes ?? 0,
        jsHeap: memory?.jsHeap ?? null,
        mapZoom,
        mode: lodMode,
        overviewBlobBytes: memory?.overviewBlobBytes ?? 0,
        visibleTileCount: visibleTiles.length,
      });
    };

    publishMemoryDebug();
    const intervalId = window.setInterval(publishMemoryDebug, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    cogOrthoApi,
    lodMode,
    mapZoom,
    reportLodDebug,
    sessionKey,
    setOrthoLodDebug,
    visibleTiles.length,
  ]);

  // Detail-tile sync: when zoomed in, decode and mount full-res COG windows for the
  // viewport. Overview layer underneath stays visible until detail tiles paint on top.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (
      orthoId == null ||
      !wantDetail ||
      map == null ||
      cogOrthoApi == null ||
      orthoOverlay == null
    ) {
      setVisibleTiles([]);
      return;
    }

    let isCancelled = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const syncVisibleTiles = async (_reason: string) => {
      // Ignore stale syncs when a newer pan/zoom or effect cleanup superseded this run.
      const generation = tileLoadGenerationRef.current + 1;
      tileLoadGenerationRef.current = generation;
      const mapBounds = map.getBounds();
      if (mapBounds == null) {
        return;
      }

      const loadedById = new globalThis.Map<string, OrthoDetailTile>();
      const isCurrent = () =>
        !isCancelled && generation === tileLoadGenerationRef.current;

      try {
        // Transform is already on the session (live COG or IndexedDB overview).
        // loadDetailTilesById opens the COG only for tiles missing from memory and IndexedDB.
        const neededIds = cogOrthoApi.listVisibleDetailTileIds(
          mapBounds,
          VIEWPORT_TILE_RETAIN_RATIO,
        );

        // Merge decoded tiles into React state. Only adds sources — never unmounts —
        // so pan/zoom does not flash the low-res overview underneath.
        const publishTiles = (_source: string) => {
          if (!isCurrent() || !wantDetailRef.current) {
            return;
          }
          setVisibleTiles((current) => {
            const byId = new globalThis.Map<string, OrthoDetailTile>();
            for (const tile of current) {
              byId.set(tile.id, tile);
            }
            const addedIds: string[] = [];
            for (const [tileId, tile] of loadedById) {
              if (!byId.has(tileId)) {
                addedIds.push(tileId);
              }
              byId.set(tileId, tile);
            }
            if (addedIds.length === 0 && byId.size === current.length) {
              return current;
            }

            return [...byId.values()];
          });
        };

        // Mount anything already in the COG detail cache (no network decode).
        for (const tileId of neededIds) {
          const cached = cogOrthoApi.getCachedDetailTile(tileId);
          if (cached != null) {
            loadedById.set(tileId, cached);
          }
        }
        publishTiles("cache-warm");

        const missingIds = neededIds.filter(
          (tileId) => loadedById.get(tileId) == null,
        );
        if (missingIds.length === 0) {
          cogOrthoApi.pinDetailTiles(neededIds);
          return;
        }

        // Fetch missing windows via COG Range requests; publish each tile as it decodes.
        await cogOrthoApi.loadDetailTilesById(neededIds, {
          onTile: (tile) => {
            if (!isCurrent()) {
              return;
            }
            loadedById.set(tile.id, tile);
            publishTiles(`tile:${tile.id}`);
            void preloadImageUrls([tile.imageUrl]).catch((error: unknown) => {
              console.error(
                "[CogOrthoMapLayers] detail tile preload failed:",
                JSON.stringify(
                  error instanceof Error
                    ? {
                        message: error.message,
                        name: error.name,
                        tileId: tile.id,
                      }
                    : { error, tileId: tile.id },
                ),
              );
            });
          },
          shouldContinue: isCurrent,
        });

        if (!isCurrent()) {
          return;
        }

        // Protect viewport tiles from LRU eviction while detail LOD is active.
        cogOrthoApi.pinDetailTiles(neededIds);
        publishTiles("sync-complete");
      } catch (error) {
        console.error(
          "[CogOrthoMapLayers] Failed to load COG ortho detail tiles:",
          JSON.stringify(
            error instanceof Error
              ? { message: error.message, name: error.name, stack: error.stack }
              : error,
          ),
        );
      }
    };

    const scheduleSync = (reason: string) => {
      if (debounceTimer != null) {
        clearTimeout(debounceTimer);
      }
      // Wait for viewport to settle before starting/decoding windows.
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void syncVisibleTiles(reason);
      }, 200);
    };

    scheduleSync("want-detail-enter");

    const handleMoveEnd = () => {
      if (!wantDetailRef.current) {
        return;
      }
      scheduleSync("moveend");
    };

    map.on("moveend", handleMoveEnd);
    return () => {
      isCancelled = true;
      if (debounceTimer != null) {
        clearTimeout(debounceTimer);
      }
      map.off("moveend", handleMoveEnd);
    };
  }, [cogOrthoApi, mapRef, orthoId, orthoOverlay, sessionKey, wantDetail]);

  return (
    <>
      <Source
        data={orthoOverlay?.outlineGeoJson ?? EMPTY_FEATURE_COLLECTION}
        id={ORTHOPHOTO_OUTLINE_SOURCE_ID}
        type="geojson"
      >
        <Layer
          beforeId={BOUNDARY_LINE_LAYER_ID}
          id={ORTHOPHOTO_OUTLINE_LAYER_ID}
          layout={{ visibility: layerVisibility }}
          paint={{
            "line-color": "#00e676",
            "line-dasharray": [2, 1],
            "line-width": 3,
          }}
          type="line"
        />
      </Source>
      <Source
        coordinates={orthoOverlay?.coordinates ?? EMPTY_ORTHO_IMAGE_COORDINATES}
        id={ORTHOPHOTO_OVERVIEW_SOURCE_ID}
        type="image"
        url={orthoOverlay?.imageUrl ?? EMPTY_ORTHO_IMAGE_URL}
      >
        <Layer
          beforeId={ORTHOPHOTO_OUTLINE_LAYER_ID}
          id={ORTHOPHOTO_IMAGE_LAYER_ID}
          layout={{ visibility: layerVisibility }}
          paint={{ "raster-opacity": 1 }}
          type="raster"
        />
      </Source>
      {orthoId != null &&
        visibleTiles.map((tile) => (
          <Source
            key={tile.id}
            coordinates={tile.coordinates}
            id={getDetailTileSourceId(tile.id)}
            type="image"
            url={tile.imageUrl}
          >
            <Layer
              beforeId={ORTHOPHOTO_OUTLINE_LAYER_ID}
              id={getDetailTileLayerId(tile.id)}
              layout={{ visibility: layerVisibility }}
              paint={{ "raster-opacity": 1 }}
              type="raster"
            />
          </Source>
        ))}
    </>
  );
};

export default CogOrthoMapLayers;
